import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { SignedAvatarImage } from "@/components/signed-avatar-image";
import { Button } from "@/components/ui/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/lib/swr";
import { useIsAdmin } from "@/hooks/auth";
import { useApps, useAppCatalog } from "@/hooks/apps";
import { CreateAppDialog } from "@/screens/Apps/CreateAppDialog";
import { Link, NavLink, useMatch } from "react-router-dom";
import { Fragment, type ReactNode } from "react";
import { LogOutIcon } from "lucide-react";
import { useMyTags } from "@/hooks/tags";
import { useInboxCount } from "@/hooks/inbox";
import { cn } from "@/lib/utils";
import { SidebarNavGroup } from "@/components/nav/sidebar-nav-group";
import { UNGROUPED, groupAppsByGroup } from "@/constants/app-groups";
import { BREADCRUMB_SLOT_ID } from "@/components/page-breadcrumb";

interface MenuItem {
  path: string;
  title: string;
  end?: boolean;
}

// Route-driven active state via SidebarMenuButton's isActive prop (the shadcn
// data-active styling). NavLink's className callback can't be used here:
// Radix Slot (asChild) string-joins className props, which stringifies the
// callback and drops the active class entirely.
const NavItemButton = ({
  to,
  end,
  forceActive,
  children,
}: {
  to: string;
  end?: boolean;
  forceActive?: boolean;
  children: ReactNode;
}) => {
  const match = useMatch({ path: to, end: end ?? false });
  return (
    <SidebarMenuButton
      asChild
      isActive={!!match || !!forceActive}
      className="data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:hover:bg-primary/90 data-[active=true]:hover:text-primary-foreground"
    >
      <NavLink to={to} end={end}>
        {children}
      </NavLink>
    </SidebarMenuButton>
  );
};

const SidebarLink = ({
  menu,
  badge,
}: {
  menu: MenuItem;
  badge?: ReactNode;
}) => (
  <SidebarMenuItem>
    <NavItemButton to={menu.path} end={menu.end}>
      <span className="truncate">{menu.title}</span>
    </NavItemButton>
    {badge}
  </SidebarMenuItem>
);

// Inbox total on the Inbox entry; turns destructive once anything is overdue.
// Hidden at zero so an empty inbox reads as "nothing to do".
const InboxBadge = () => {
  const { data } = useInboxCount();
  if (!data?.total) return null;
  return (
    <SidebarMenuBadge
      className={cn(
        data.overdue > 0
          ? "bg-destructive text-destructive-foreground peer-data-[active=true]/menu-button:text-destructive-foreground peer-hover/menu-button:text-destructive-foreground"
          : "bg-sidebar-accent"
      )}
    >
      {data.total}
    </SidebarMenuBadge>
  );
};

// Apps as a second-level menu: parent "Apps" expands to the individual apps
// (server-scoped to the user's visible apps). Creating an app is open to
// everyone (id/code auto-assigned server-side; the creator gets visibility
// on the new app automatically), so the group and its "+" always show.
// Apps are clustered by their `group` field (ungrouped last, no header when
// it's the only cluster); each row shows "{pid} - {name}".
const AppsNavGroup = () => {
  const apps = useApps();
  const grouped = groupAppsByGroup(apps);
  return (
    <SidebarNavGroup
      label="Apps"
      items={grouped}
      hideWhenEmpty={false}
      headerAction={<CreateAppDialog />}
      renderItem={([group, groupApps]) => (
        <Fragment key={group}>
          {(group !== UNGROUPED || grouped.length > 1) && (
            <SidebarGroupLabel asChild className="h-6">
              <li>{group}</li>
            </SidebarGroupLabel>
          )}
          {groupApps.map((a) => (
            <SidebarMenuItem key={a.id}>
              <NavItemButton to={`/apps/${a.id}`}>
                <span className="truncate">
                  {a.id} - {a.name}
                </span>
                {/* App mark: real client apps stand out from project-type
                    entries (Monday/Wednesday/…). Inherits the item's text
                    color (dimmed) so it stays legible on the active
                    primary background too. */}
                {a.isApp && (
                  <span className="ml-auto shrink-0 text-[10px] uppercase opacity-60">
                    app
                  </span>
                )}
              </NavItemButton>
            </SidebarMenuItem>
          ))}
        </Fragment>
      )}
    />
  );
};

// Labels the user owns or joined (server-side membership). Only shown when
// non-empty; click into /tags/:id to see the PRDs under that label.
const TagsNavGroup = () => {
  const tags = useMyTags();
  return (
    <SidebarNavGroup
      label="Labels"
      items={tags}
      hideWhenEmpty
      renderItem={(t) => (
        <SidebarMenuItem key={t.id}>
          <NavItemButton to={`/tags/${t.id}`}>
            <span className="truncate">#{t.name}</span>
          </NavItemButton>
        </SidebarMenuItem>
      )}
    />
  );
};

const Logo = ({ iconClass = "h-8 w-8" }: { iconClass?: string }) => (
  <Link to="/" className="flex items-center gap-2">
    <img src="/logo256.png" alt="Logo" className={iconClass} />
    <span className="font-bold text-primary">Wednesday</span>
  </Link>
);

// Content-area header (official shadcn layout): SidebarTrigger + separator +
// page breadcrumb on one row. The trigger stays in the same spot whether the
// sidebar is expanded or collapsed, desktop or mobile (mobile also shows the
// logo and opens the sidebar as a Sheet via SidebarProvider). Detail pages'
// <PageBreadcrumb> portals itself into the slot div here.
export const AppHeader = () => (
  <header className="sticky top-0 z-40 flex h-12 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur md:static md:z-auto md:border-0 md:bg-transparent md:backdrop-blur-none">
    <SidebarTrigger className="-ml-1 text-muted-foreground" />
    <div
      id={BREADCRUMB_SLOT_ID}
      className="flex min-w-0 items-center gap-2"
    />
    <div className="md:hidden">
      <Logo iconClass="h-7 w-7" />
    </div>
  </header>
);

// App sidebar on the shadcn Sidebar primitives: SidebarProvider (in App.tsx)
// owns open/collapsed state — cookie-persisted, Cmd/Ctrl+B toggles, mobile
// renders as a Sheet automatically. Desktop collapse = offcanvas + edge rail.
const AppSidebar = () => {
  const user = useAuthStore((store) => store.auth?.user);
  const removeToken = useAuthStore((store) => store.removeToken);
  const isAdmin = useIsAdmin();
  const { data: catalog } = useAppCatalog();
  const canManageApps = user?.kind !== "bot" && (
    isAdmin || catalog?.some((app) => app.ownerIds?.includes(String(user?.id)))
  );

  if (!user) return null;

  return (
    <>
      <Sidebar collapsible="offcanvas">
        <SidebarHeader className="flex-row items-center py-3 pl-3 pr-2">
          <Logo />
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup className="py-1">
            <SidebarGroupContent>
              <SidebarMenu>
                {/* "/" = Inbox (everything waiting on me); /users/:id/* is
                    the profile page for viewing someone else. */}
                <SidebarLink
                  menu={{ path: "/", title: "Inbox", end: true }}
                  badge={<InboxBadge />}
                />
                {/* Every PRD under the user's visible apps */}
                <SidebarLink menu={{ path: "/prds", title: "PRDs" }} />
                <SidebarLink menu={{ path: "/exec-templates", title: "Prompt 模板" }} />
                {/* Audits lives as a tab under /versions */}
                <SidebarLink menu={{ path: "/versions", title: "Versions" }} />
                {/* RC config switches (A/B tests / deploys / ops), cross-app */}
                <SidebarLink menu={{ path: "/rcs", title: "RC Configs" }} />
                {canManageApps && (
                  <SidebarLink menu={{ path: "/apps/archived", title: "Archived apps" }} />
                )}
                {isAdmin && (
                  <SidebarLink menu={{ path: "/admin", title: "Admin" }} />
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          <TagsNavGroup />
          <AppsNavGroup />
        </SidebarContent>

        <SidebarFooter className="border-t">
          {/* Avatar + name opens my own user page (PRD 79a77e23); logging
              out is its own button so the two never share a click target. */}
          <div className="flex items-center gap-1">
            <Button
              asChild
              variant="ghost"
              className="h-auto min-w-0 flex-1 justify-start gap-2 px-3 py-2 text-sm font-normal text-foreground/70"
            >
              <Link to={`/users/${user.id}`} aria-label="My page">
                <Avatar className="h-6 w-6">
                  <SignedAvatarImage src={user.avatarThumb} alt="user avatar" />
                  <AvatarFallback className="text-xs">
                    {user.name?.[0] ?? "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{user.name}</span>
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={removeToken}
              aria-label="Log out"
              title="Log out"
              className="h-8 w-8 shrink-0 text-foreground/70"
            >
              <LogOutIcon />
            </Button>
          </div>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>
    </>
  );
};

export default AppSidebar;
